import Axios from 'axios';
import api, { publicApi } from '../../config/AxiosService';
import {
  Batch,
  Client,
  Inference,
  InferenceFile,
  InferenceStatus,
  Project,
  TestResult,
  TestResultRow,
  Pagination,
} from '../../types';
import { ClientScoring, Position } from '../../types/result';
import { TransformedLabel } from '../../types/label';

//GENERAL

export const getClients = async (): Promise<Client[]> => {
  const response = await api.get(`/inbox-overview/clients`);
  return response.data;
};

export const getProjects = async (): Promise<Project[]> => {
  const response = await api.get('/projects/list');
  return response.data;
};

export const getProject = async (id: string): Promise<Project> => {
  const response = await api.get(`/projects/${id}`);
  return response.data;
};

//INFERENCE

export const getInferenceList = async (
  algorithmId: string,
  params: Pagination,
  filter: string
): Promise<{ totalItems: number; items: InferenceFile[] }> => {
  const response = await api.get(
    `/inference-files/list/${algorithmId}?page=${params.page}&size=${params.pageSize}` +
      (filter ? `&name=${filter}` : '')
  );
  return { items: response.data.items, totalItems: response.data.meta.totalItems };
};

export const getInferenceListStatus = async (modelId: string): Promise<InferenceStatus[]> => {
  const response = await api.get(`/inference/list/${modelId}/status`);
  return response.data;
};

export const setFileAnnotations = async (
  inferenceFileId: string,
  annotations: {
    type?: { reliable: boolean; name: string };
    fields: TransformedLabel[];
  }
) => {
  const response = await api.put('/inference-files/' + inferenceFileId + '/annotations', annotations, {
    headers: { 'Content-Type': 'application/json' },
  });
  console.log(response);
};

//deprecated
export const getTestFile = async (fileId: string): Promise<InferenceFile> => {
  const response = await api.get('/inference-files/' + fileId);
  return response.data;
};

export const getInference = async (inferenceId: string): Promise<Inference> => {
  const response = await api.get('/inference-files/' + inferenceId);
  return response.data;
};

export const startInferenceBatch = async (modelId: number, batchCreationInfo: Batch) => {
  const response = await api.post(`/inference/start-inference/${modelId}`, batchCreationInfo, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

export const startInferenceBatchByModelVersion = async (modelVersionId: string, batchCreationInfo: Batch) => {
  const response = await api.post(`/inference/start-by-model-version/${modelVersionId}`, batchCreationInfo, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

export const startMultiFlowInferenceBatch = async (modelId: number, batchCreationInfo: Batch, open: boolean) => {
  const response = await (open ? publicApi : api).post(
    `/inference/start-multiflow-inference/${modelId}`,
    batchCreationInfo,
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );
  return response.data;
};

export const getPresignedURLs = async (url: string, data: object, open: boolean): Promise<Batch> => {
  const response = await (open ? publicApi : api).post(url, data);
  return response.data;
};

export const getApiPresignedURLs = async (fileNames: string[]): Promise<Batch> => {
  const response = await api.post('/aws/pre-signed-urls', {
    fileNames,
  });
  return response.data;
};

export const getWord = async (fileId: string, page: number, location: Position, dataset = false) => {
  if (!dataset) {
    const { data } = await api.get(
      `/inference/get-word?fileId=${fileId}&page=${page}&x1=${location.x1}&x2=${location.x2}&y1=${location.y1}&y2=${location.y2}`
    );
    return data.word;
  } else {
    const { data } = await api.get(
      `/dataset-files/get-word?fileId=${fileId}&page=${page}&x1=${location.x1}&x2=${location.x2}&y1=${location.y1}&y2=${location.y2}`
    );
    return data.word;
  }
};

export const deleteInference = async (inferenceId: string) => {
  await api.delete(`/inference-files/${inferenceId}`);
};

//SCORING

export const getScoringOverview = async (): Promise<ClientScoring[]> => {
  const response = await api.get(`/scoring-overview`);
  return response.data;
};

export const removeDatasetToScoringAlgorithm = async (clientScoringId: string) => {
  await api.delete(`/scoring-overview/${clientScoringId}`);
};

export const addDatasetToScoringAlgorithm = async (datasetId: string, algorithmId: string) => {
  await api.post(`/scoring-overview`, { customer_name: 'test', dataset_id: datasetId, algorithm_id: algorithmId });
};

export const getTestResultFiles = async (
  algorithmVersionId: string,
  datasetVersionId: string
): Promise<TestResultRow[]> => {
  const request = await api.get(`/scoring/details/${algorithmVersionId}/${datasetVersionId}`);
  const response = await Axios.get(request.data.url);
  return response.data.details;
};

export const getTestResultFileDetails = async (
  algorithmVersionId: string,
  datasetVersionId: string,
  fileId: string
): Promise<TestResult> => {
  const request = await api.get(`/scoring/details/${algorithmVersionId}/${datasetVersionId}/${fileId}`);
  const response = await Axios.get(request.data.url);
  return response.data;
};
